// Katalog — kırılım akışları (spec §6.4 DR6/DR14; KpiCatalogPage 600 satır sınırı):
// (1) "Kırılıma dönüştür" satır eylemi → BreakdownConvertDialog (store `convertCatalogBreakdown`);
// başarıda katalog yeniden yüklenir, başarı paneli pencerede kalır; 422 birim → üye göstergesi
// MetricModal'da (katalog birimi) ya da müşteri kaydı varsa Veri Yönetimi'nde açılır.
// (2) MetricModal "Yeni grup…" / "+ Kırılım grubu ekle" → BreakdownGroupModal (katalog bağlamı: set
// yok, müşteri yok). İki modal üst üste açılmaz (Esc ikisini birden kapatırdı): MetricModal taslağı
// burada saklanır, modal kapanır; grup kaydı/vazgeç sonrası taslak geri verilir — "Yeni grup…"
// kaynaklıysa üst = yeni grup (üye kipi).

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { breakdownFamilyRootOf, type KpiMetric } from "@mahir/shared";
import { BreakdownConvertDialog } from "../../components/kpi/BreakdownConvertDialog";
import { BreakdownGroupModal } from "../../components/kpi/BreakdownGroupModal";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { rootCodeOf } from "../../lib/kpi-breakdown-dimension-ui";
import { type GroupFormContext } from "../../lib/kpi-breakdown-group-form";
import { memberSlugFor, type ConvertPattern } from "../../lib/kpi-catalog-breakdown";
import { type MetricDraft } from "../../lib/kpi-metric-draft";
import { kpiSetManagementHref } from "../../lib/kpi-ui";
import { useKpiStore } from "../../stores/kpi-store";

interface GroupTarget {
  totalId: string;
  draft: MetricDraft;
  /** "location" = MetricModal Konum "Yeni grup…" (taslak yeni grubun üyesi olur) */
  origin: "location" | "panel";
  /** kaydedildi, katalog yükleniyor: grup modalı kapalı, MetricModal henüz açılmaz */
  saved?: boolean;
}

export function useCatalogBreakdown(args: {
  metrics: readonly KpiMetric[] | null;
  reload: () => Promise<void>;
  onFlash: (message: string) => void;
  /** MetricModal'ı bu taslakla yeniden aç */
  restoreDraft: (draft: MetricDraft) => void;
  /** göstergeyi MetricModal'da aç (422 birim — katalog birimi) */
  openMetric: (metricId: string) => void;
}): {
  openConvert: (metric: KpiMetric, pattern: ConvertPattern) => void;
  openGroup: (totalId: string, draft: MetricDraft, origin: GroupTarget["origin"]) => void;
  groupOpen: boolean;
  dialogs: JSX.Element;
} {
  const navigate = useNavigate();
  const convertSubmit = useKpiStore((s) => s.convertCatalogBreakdown);
  const groupSubmit = useKpiStore((s) => s.addCatalogBreakdownGroup);
  const [convertFor, setConvertFor] = useState<{
    metric: KpiMetric;
    pattern: ConvertPattern;
  } | null>(null);
  const [groupFor, setGroupFor] = useState<GroupTarget | null>(null);
  const metrics = args.metrics;

  const ctx = useMemo<GroupFormContext | null>(() => {
    if (!groupFor || !metrics) return null;
    const picked = metrics.find((m) => m.id === groupFor.totalId);
    if (!picked) return null;
    // İkinci seviye (K-B9): hedef bir seviye-1 üyeyse grup onun altına kurulur; `total` aile kökü.
    const byId = new Map(metrics.map((m) => [m.id, m]));
    const memberGroup =
      picked.dimensionMember !== null && picked.parentId ? byId.get(picked.parentId) : undefined;
    const rootId = memberGroup ? breakdownFamilyRootOf(picked.id, (id) => byId.get(id)) : picked.id;
    const total = byId.get(rootId) ?? picked;
    const used = metrics
      .filter((m) => m.parentId === picked.id && m.nodeKind === "breakdown_group")
      .map((g) => g.breakdown?.dimension)
      .filter((d): d is string => !!d);
    return {
      total: { metricId: total.id, code: total.code, label: total.label, unit: total.defaultUnit },
      ...(memberGroup?.nodeKind === "breakdown_group"
        ? {
            parentMember: {
              label: picked.label,
              groupLabel: memberGroup.label,
              metricId: picked.id,
            },
          }
        : {}),
      group: null,
      usedDimensions: new Set(used),
      calcGroupLabel: null,
      totalClientId: picked.clientId,
      rootCode: rootCodeOf(total.id, metrics),
    };
  }, [groupFor, metrics]);

  function closeGroup(groupId: string | null): void {
    const g = groupFor;
    setGroupFor(null);
    if (!g) return;
    args.restoreDraft(
      groupId && g.origin === "location"
        ? {
            ...g.draft,
            parentId: groupId,
            dimensionMember: g.draft.dimensionMember ?? memberSlugFor(g.draft.label),
          }
        : g.draft,
    );
  }

  const dialogs = (
    <>
      {convertFor && metrics ? (
        <BreakdownConvertDialog
          key={convertFor.metric.id}
          source={convertFor.metric}
          pattern={convertFor.pattern}
          catalog={metrics}
          submit={convertSubmit}
          onConverted={() => void args.reload()}
          onGoToUnit={(metricId, clientId) => {
            setConvertFor(null);
            if (clientId) void navigate(kpiSetManagementHref(clientId, null));
            else if (metricId) args.openMetric(metricId);
          }}
          onClose={() => setConvertFor(null)}
        />
      ) : null}
      {groupFor && !groupFor.saved && ctx ? (
        <BreakdownGroupModal
          key={groupFor.totalId}
          ctx={ctx}
          setId={null}
          clientId={null}
          clientName=""
          catalogMembers={[]}
          existingMembers={[]}
          submit={groupSubmit}
          onSaved={(r, label) => {
            args.onFlash(KPI_BREAKDOWN_COPY.modal.success(label, r.memberIds.length));
            // önce katalog (yeni grup listede olsun — radyolar boş görünmesin), sonra taslak
            setGroupFor({ ...groupFor, saved: true });
            void args
              .reload()
              .catch(() => undefined)
              .then(() => closeGroup(r.groupId));
          }}
          onClose={() => closeGroup(null)}
        />
      ) : null}
    </>
  );

  return {
    openConvert: (metric, pattern) => setConvertFor({ metric, pattern }),
    openGroup: (totalId, draft, origin) => setGroupFor({ totalId, draft, origin }),
    groupOpen: groupFor !== null,
    dialogs,
  };
}
